import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useQuery, useQueryClient} from 'react-query'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import {toast} from 'react-toastify'
import dataAxios from '../../modules/auth/core/dataAxios'
import formDataAxios from '../../modules/auth/core/formDataAxios'
import StickyLanguageSelector from '../../components/StickyLanguageSelector'
import {KTCard, KTCardBody} from '../../../_dashboard/helpers'
import {mergeUniqueTags, parseTagInput} from '../tours-management/tour-details/core/tagInputHelpers'
import {recommendedSizeFor} from '../../utils/imagePlacements'

type BlogStatus = 'draft' | 'published'

type BlogTranslation = {
  _id?: string
  language: string
  title: string
  content: string
  excerpt: string
  tags: string[]
  metaTitle: string
  metaDescription: string
  metaKeywords: string
}

type BlogFormValues = {
  slug: string
  status: BlogStatus
  publishedAt: string
  category: string
  readingTime: number
  translations: BlogTranslation[]
}

const defaultTranslation = (language: string): BlogTranslation => ({
  language,
  title: '',
  content: '',
  excerpt: '',
  tags: [],
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
})

const initialValues: BlogFormValues = {
  slug: '',
  status: 'draft',
  publishedAt: '',
  category: '',
  readingTime: 1,
  translations: [defaultTranslation('fr')],
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')

const normalizeLang = (code: unknown): string =>
  String(code ?? '')
    .trim()
    .toLowerCase()
    .split('-')[0]

const htmlToPlainText = (html: string) =>
  String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const translationHasBody = (translation: BlogTranslation) =>
  Boolean((translation.title || '').trim() || htmlToPlainText(translation.content))

type GallerySlot = {
  key: string
  preview: string
  existingId?: string
  file?: File
}

const blogImageDisplayUrl = (url: string) => {
  if (!url) return ''
  if (url.startsWith('blob:') || url.startsWith('http') || url.startsWith('data:')) return url
  return url.includes('/images/') ? `${process.env.REACT_APP_API_URL_IMAGE || ''}${url}` : url
}

const IMAGE_NAME_RE = /\.(jpe?g|png|gif|webp|bmp)$/i

const isImageFile = (file: File) => {
  if (file.type && file.type.startsWith('image/')) return true
  return IMAGE_NAME_RE.test(file.name)
}

const collectBlogGallery = (blog: any): GallerySlot[] => {
  const slots: GallerySlot[] = []
  const seen = new Set<string>()
  const push = (img: any) => {
    if (!img) return
    const id = String(typeof img === 'object' ? img._id : img)
    if (!id || id === 'undefined' || seen.has(id)) return
    seen.add(id)
    const url = typeof img === 'object' ? String(img.url || '') : ''
    slots.push({
      key: id,
      preview: blogImageDisplayUrl(url),
      existingId: id,
    })
  }
  push(blog?.image)
  ;(Array.isArray(blog?.images) ? blog.images : []).forEach(push)
  return slots
}

const estimateReadingMinutes = (htmlContent: string): number => {
  const plainText = htmlContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
  const words = plainText ? plainText.split(' ').length : 0
  return Math.max(1, Math.ceil(words / 200))
}

const ensureTranslationForLanguage = (
  translations: BlogTranslation[],
  language: string,
): BlogTranslation[] => {
  const lang = normalizeLang(language) || 'fr'
  const list = (translations || []).map((t) => ({
    ...t,
    language: normalizeLang(t.language) || t.language,
  }))
  if (list.some((t) => normalizeLang(t.language) === lang)) return list
  const source = list.find((t) => Array.isArray(t.tags) && t.tags.length) || list[0]
  return [
    ...list,
    {
      ...defaultTranslation(lang),
      tags: Array.isArray(source?.tags) ? [...source.tags] : [],
    },
  ]
}

const BlogEditPage: React.FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {id} = useParams<{id: string}>()
  const isEditMode = Boolean(id && id !== 'new')
  const [selectedLanguage, setSelectedLanguage] = useState('fr')
  const selectedLanguageRef = useRef(selectedLanguage)
  selectedLanguageRef.current = selectedLanguage
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(['fr', 'en', 'de'])
  const [gallery, setGallery] = useState<GallerySlot[]>([])
  const [tagInput, setTagInput] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [mainImageMissingAttempt, setMainImageMissingAttempt] = useState(false)
  const [isImageDropActive, setIsImageDropActive] = useState(false)
  const quillRef = useRef<ReactQuill | null>(null)
  const mainImageSectionRef = useRef<HTMLDivElement | null>(null)
  const addImagesInputRef = useRef<HTMLInputElement | null>(null)
  const galleryHydratedForId = useRef<string | null>(null)
  const galleryRef = useRef<GallerySlot[]>([])
  galleryRef.current = gallery

  const {data: categoriesData} = useQuery(['blog-categories-public', selectedLanguage], async () => {
    const res = await dataAxios.get('/blog-categories/public', {params: {language: selectedLanguage}})
    const rows = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []
    return rows.filter((c: {status?: string}) => c.status !== 'inactive')
  })

  const {data: tagsSuggestions} = useQuery(['blog-tags-public', selectedLanguage], async () => {
    const res = await dataAxios.get<string[]>('/blogs/public/tags', {params: {language: selectedLanguage}})
    return Array.isArray(res.data) ? res.data : []
  })

  useQuery('available-languages-blog-edit', async () => {
    const res = await dataAxios.get('/languages')
    const raw = Array.isArray(res.data)
      ? res.data
      : Array.isArray(res.data?.data)
        ? res.data.data
        : []
    const codes = raw.map((code: unknown) => normalizeLang(code)).filter((code: string) => /^[a-z]{2}$/.test(code))
    if (codes.length) {
      setAvailableLanguages(codes)
    }
    return codes
  })

  const {data: blogDetails, isLoading: isLoadingBlog} = useQuery(
    ['blog-by-id-all-languages', id],
    async () => {
      const res = await dataAxios.get(`/blogs/${id}?language=all`)
      return res.data?.data ?? res.data
    },
    {enabled: isEditMode, refetchOnWindowFocus: false},
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        slug: Yup.string()
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.REQUIRED_FIELD'}))
          .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be URL-safe (lowercase, no spaces)'),
        status: Yup.string().oneOf(['draft', 'published']).required(),
        publishedAt: Yup.string().when('status', {
          is: 'published',
          then: (schema) => schema.required(intl.formatMessage({id: 'AUTH.VALIDATION.REQUIRED_FIELD'})),
        }),
        translations: Yup.array().of(
          Yup.object().shape({
            language: Yup.string(),
            title: Yup.string(),
            content: Yup.string(),
            excerpt: Yup.string().max(500),
          }),
        ),
      }),
    [intl],
  )

  const formik = useFormik<BlogFormValues>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (!isEditMode && galleryRef.current.length === 0) {
          setMainImageMissingAttempt(true)
          mainImageSectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
          toast.error(intl.formatMessage({id: 'COMMON.ERROR.NO_IMAGE_SELECTED'}))
          return
        }

        const formData = new FormData()
        if (isEditMode) {
          formData.append('replaceImages', 'true')
          galleryRef.current.forEach((slot) => {
            if (slot.existingId) formData.append('existingImageIds', slot.existingId)
          })
        }
        galleryRef.current.forEach((slot) => {
          if (slot.file) formData.append('images', slot.file, slot.file.name)
        })
        formData.append('slug', values.slug.trim())
        formData.append('status', values.status)
        if (values.status === 'published') {
          const publishedValue = values.publishedAt || new Date().toISOString()
          formData.append('publishedAt', publishedValue)
        }
        if (values.category.trim()) {
          formData.append('category', values.category.trim())
        }
        const tagsByLanguage: Record<string, string[]> = {}
        values.translations.forEach((translation) => {
          const lang = normalizeLang(translation.language)
          if (!lang) return
          tagsByLanguage[lang] = Array.isArray(translation.tags) ? translation.tags : []
        })
        const allTags = Array.from(new Set(Object.values(tagsByLanguage).flat()))
        formData.append('tags', JSON.stringify(allTags))
        formData.append('tagsByLanguage', JSON.stringify(tagsByLanguage))
        formData.append('readingTime', String(values.readingTime || 1))
        formData.append('isActive', 'true')

        const translationsToSave = values.translations.filter(translationHasBody)
        if (!translationsToSave.length) {
          toast.error(intl.formatMessage({id: 'AUTH.VALIDATION.REQUIRED_FIELD'}))
          return
        }

        translationsToSave.forEach((translation, index) => {
          if (translation._id) formData.append(`translations[${index}][_id]`, String(translation._id))
          formData.append(
            `translations[${index}][language]`,
            normalizeLang(translation.language) || selectedLanguageRef.current || 'fr',
          )
          formData.append(`translations[${index}][title]`, translation.title || '')
          formData.append(`translations[${index}][content]`, translation.content || '')
          formData.append(`translations[${index}][excerpt]`, translation.excerpt || '')
          const tags = Array.isArray(translation.tags) ? translation.tags : []
          formData.append(`translations[${index}][tags]`, JSON.stringify(tags))
          formData.append(`translations[${index}][metaTitle]`, translation.metaTitle || '')
          formData.append(`translations[${index}][metaDescription]`, translation.metaDescription || '')
          formData.append(`translations[${index}][metaKeywords]`, translation.metaKeywords || '')
          formData.append(
            `translations[${index}][openGraphTitle]`,
            translation.metaTitle || '',
          )
          formData.append(
            `translations[${index}][openGraphDescription]`,
            translation.metaDescription || '',
          )
        })

        if (isEditMode) {
          await formDataAxios.patch(`/blogs/${id}`, formData)
          toast.success(intl.formatMessage({id: 'TOUR.UPDATED_SUCCESSFULLY', defaultMessage: 'Updated'}))
          galleryHydratedForId.current = null
          await queryClient.invalidateQueries(['blog-by-id-all-languages', id])
        } else {
          await formDataAxios.post('/blogs', formData)
          toast.success(intl.formatMessage({id: 'TOUR.ADDED_SUCCESSFULLY', defaultMessage: 'Created'}))
          navigate('/apps/blog-management')
        }
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || 'Save failed'
        toast.error(String(message))
      }
    },
  })

  /** setFieldValue + submitForm() dans le même clic laisse souvent un état Formik obsolète (validation / onSubmit). */
  const submitBlogWithStatus = async (nextStatus: BlogStatus) => {
    if (!isEditMode && gallery.length === 0) {
      setMainImageMissingAttempt(true)
      mainImageSectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
      toast.error(intl.formatMessage({id: 'COMMON.ERROR.NO_IMAGE_SELECTED'}))
      return
    }
    setMainImageMissingAttempt(false)

    const lang = normalizeLang(selectedLanguageRef.current) || 'fr'
    const translations = ensureTranslationForLanguage(formik.values.translations, lang)
    const selectedTranslation = translations.find((t) => normalizeLang(t.language) === lang)
    const selectedTitle = (selectedTranslation?.title || '').trim()
    const selectedBody = htmlToPlainText(selectedTranslation?.content || '')
    if (!selectedTitle || !selectedBody) {
      toast.error(intl.formatMessage({id: 'BLOG.FIX_VALIDATION_ERRORS'}))
      return
    }

    const slug = (formik.values.slug || slugify(selectedTitle)).trim()
    if (!slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      toast.error(intl.formatMessage({id: 'BLOG.FIX_VALIDATION_ERRORS'}))
      return
    }

    const filledTranslations = translations.filter(translationHasBody)
    const today = new Date().toISOString().slice(0, 10)
    const publishedAt =
      nextStatus === 'published' ? formik.values.publishedAt || today : formik.values.publishedAt

    await formik.setValues(
      {
        ...formik.values,
        slug,
        status: nextStatus,
        publishedAt,
        translations: filledTranslations,
      },
      false,
    )
    await new Promise<void>((resolve) => {
      setTimeout(resolve, 0)
    })

    await formik.submitForm()
  }

  const activeTranslationIndex = useMemo(() => {
    const lang = normalizeLang(selectedLanguage) || 'fr'
    return formik.values.translations.findIndex((t) => normalizeLang(t.language) === lang)
  }, [formik.values.translations, selectedLanguage])

  const activeTranslation =
    activeTranslationIndex >= 0
      ? formik.values.translations[activeTranslationIndex]
      : defaultTranslation(selectedLanguage)

  useEffect(() => {
    formik.setFieldValue(
      'translations',
      ensureTranslationForLanguage(formik.values.translations, selectedLanguage),
      false,
    )
    setTagInput('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage])

  useEffect(() => {
    if (!blogDetails) return
    const translationsRaw: any[] = Array.isArray(blogDetails.translations) ? blogDetails.translations : []
    const hasAnyTranslationTags = translationsRaw.some(
      (t) => Array.isArray(t?.tags) && t.tags.length > 0,
    )
    const fallbackTags = Array.isArray(blogDetails.tags) ? blogDetails.tags : []
    const normalizedTranslations = translationsRaw
      .filter((t) => t && typeof t === 'object')
      .map((t) => ({
        _id: t?._id ? String(t._id) : t?.id ? String(t.id) : undefined,
        language: normalizeLang(t.language) || 'fr',
        title: t.title || '',
        content: t.content || '',
        excerpt: t.excerpt || '',
        tags:
          Array.isArray(t.tags) && t.tags.length
            ? t.tags
            : hasAnyTranslationTags
              ? Array.isArray(t.tags)
                ? t.tags
                : []
              : fallbackTags,
        metaTitle: t.metaTitle || '',
        metaDescription: t.metaDescription || '',
        metaKeywords: t.metaKeywords || '',
      }))

    formik.setValues({
      slug: blogDetails.slug || '',
      status: blogDetails.status === 'published' ? 'published' : 'draft',
      publishedAt: blogDetails.publishedAt ? String(blogDetails.publishedAt).slice(0, 10) : '',
      category: blogDetails.category || '',
      readingTime: Number(blogDetails.readingTime || 1),
      translations:
        normalizedTranslations.length > 0
          ? normalizedTranslations
          : ensureTranslationForLanguage([], selectedLanguage),
    })

    if (galleryHydratedForId.current !== String(blogDetails._id || '')) {
      galleryHydratedForId.current = String(blogDetails._id || '')
      setGallery(collectBlogGallery(blogDetails))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogDetails])

  useEffect(() => {
    if (slugTouched || isEditMode) return
    if (!activeTranslation?.title) return
    formik.setFieldValue('slug', slugify(activeTranslation.title), false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTranslation?.title, slugTouched, isEditMode])

  useEffect(() => {
    const minutes = estimateReadingMinutes(activeTranslation?.content || '')
    formik.setFieldValue('readingTime', minutes, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTranslation?.content])

  const setActiveTranslationField = (field: keyof BlogTranslation, value: string) => {
    const lang = normalizeLang(selectedLanguage) || 'fr'
    const translations = ensureTranslationForLanguage(formik.values.translations, lang)
    const index = translations.findIndex((t) => normalizeLang(t.language) === lang)
    if (index < 0) return
    const next = [...translations]
    next[index] = {...next[index], language: lang, [field]: value}
    formik.setFieldValue('translations', next)
  }

  const setActiveTranslationTags = (tags: string[]) => {
    const lang = normalizeLang(selectedLanguage) || 'fr'
    const translations = ensureTranslationForLanguage(formik.values.translations, lang)
    const index = translations.findIndex((t) => normalizeLang(t.language) === lang)
    if (index < 0) return
    const next = [...translations]
    next[index] = {...next[index], language: lang, tags}
    formik.setFieldValue('translations', next)
  }

  const activeTags = Array.isArray(activeTranslation?.tags) ? activeTranslation.tags : []

  const commitTagInput = (raw: string) => {
    const incoming = parseTagInput(raw)
    if (!incoming.length) return
    const lang = normalizeLang(selectedLanguage) || 'fr'
    const translations = ensureTranslationForLanguage(formik.values.translations, lang)
    const index = translations.findIndex((t) => normalizeLang(t.language) === lang)
    if (index < 0) return
    const current = Array.isArray(translations[index].tags) ? translations[index].tags : []
    const next = [...translations]
    next[index] = {
      ...next[index],
      language: lang,
      tags: mergeUniqueTags(current, incoming),
    }
    formik.setFieldValue('translations', next)
    setTagInput('')
  }

  const removeTag = (tag: string) => {
    setActiveTranslationTags(activeTags.filter((item) => item !== tag))
  }

  const addGalleryFiles = (fileList: FileList | File[]) => {
    const incoming = Array.from(fileList).filter(isImageFile)
    if (!incoming.length) return
    setMainImageMissingAttempt(false)
    setGallery((prev) => {
      const next = incoming.map((file, index) => ({
        key: `new-${Date.now()}-${index}-${file.name}`,
        preview: URL.createObjectURL(file),
        file,
      }))
      return [...prev, ...next]
    })
  }

  const removeGallerySlot = (key: string) => {
    setGallery((prev) => prev.filter((slot) => slot.key !== key))
    if (!isEditMode) setMainImageMissingAttempt(false)
  }

  const replaceGallerySlot = (key: string, file: File) => {
    if (!isImageFile(file)) return
    setMainImageMissingAttempt(false)
    setGallery((prev) =>
      prev.map((slot) =>
        slot.key === key
          ? {
              key: `new-${Date.now()}-${file.name}`,
              preview: URL.createObjectURL(file),
              file,
            }
          : slot,
      ),
    )
  }

  const onGalleryDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsImageDropActive(false)
    if (e.dataTransfer.files?.length) addGalleryFiles(e.dataTransfer.files)
  }

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike'],
          [{header: [2, 3, false]}],
          [{list: 'ordered'}, {list: 'bullet'}],
          ['blockquote', 'code-block'],
          [{align: []}],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: () => {
            const quill = quillRef.current?.getEditor()
            if (!quill) return
            const imageUrl = window.prompt('Image URL')
            if (!imageUrl) return
            const range = quill.getSelection(true)
            quill.insertEmbed(range?.index || 0, 'image', imageUrl)
          },
        },
      },
    }),
    [],
  )

  return (
    <>
      <style>{`
        .blog-editor-shell .ql-toolbar.ql-snow {
          border-top-left-radius: 0.475rem;
          border-top-right-radius: 0.475rem;
          border-color: #e4e6ef;
          background: #f9f9f9;
        }
        .blog-editor-shell .ql-container.ql-snow {
          border-bottom-left-radius: 0.475rem;
          border-bottom-right-radius: 0.475rem;
          border-color: #e4e6ef;
          min-height: 280px;
          font-family: inherit;
        }
        .blog-image-drop {
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }
        .blog-image-drop:hover {
          border-color: var(--bs-primary) !important;
          background-color: rgba(var(--bs-primary-rgb), 0.06) !important;
        }
      `}</style>

      <div className='mb-8'>
        <Link
          to='/apps/blog-management'
          className='btn btn-sm btn-light-primary fw-bold mb-4'
        >
          <i className='bi bi-arrow-left me-2' />
          {intl.formatMessage({id: 'BLOG.BACK_TO_LIST'})}
        </Link>
        <h1 className='fs-2 fw-bold text-gray-900 mb-2'>
          {intl.formatMessage({id: isEditMode ? 'BLOG.EDIT_ARTICLE' : 'BLOG.NEW_ARTICLE'})}
        </h1>
        <p className='text-gray-600 fs-6 fw-semibold mb-0'>
          {intl.formatMessage({id: 'BLOG.LIST_SUBTITLE'})}
        </p>
      </div>

      <KTCard className='shadow-sm'>
        <KTCardBody className='py-6'>
          <StickyLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
            allowedLanguageCodes={availableLanguages}
          />

          {isEditMode && isLoadingBlog ? (
            <div className='d-flex flex-column align-items-center justify-content-center py-20'>
              <span className='spinner-border text-primary mb-4' role='status' />
              <span className='text-muted fw-semibold'>{intl.formatMessage({id: 'COMMON.PROCESSING'})}</span>
            </div>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              <div className='row g-8'>
                <div className='col-lg-8'>
                  <div className='card border border-gray-200 shadow-sm mb-0'>
                    <div className='card-header border-0 pt-6 pb-0 bg-transparent'>
                      <div className='card-title m-0'>
                        <h2 className='fs-4 fw-bold text-gray-900 mb-0 d-flex align-items-center gap-3'>
                          <span className='bullet bullet-vertical h-30px bg-primary' />
                          <i className='bi bi-file-earmark-text text-primary fs-3' />
                          {intl.formatMessage({id: 'BLOG.SECTION_ARTICLE'})}
                        </h2>
                      </div>
                    </div>
                    <div className='card-body pt-5'>
                      <div className='mb-8'>
                        <label className='form-label required fw-semibold text-gray-700'>
                          {intl.formatMessage({id: 'TOUR.FIELDS.TITLE'})}
                        </label>
                        <input
                          type='text'
                          className='form-control form-control-lg form-control-solid'
                          value={activeTranslation.title}
                          onChange={(e) => setActiveTranslationField('title', e.target.value)}
                          placeholder={intl.formatMessage({id: 'TOUR.FIELDS.ENTER_TITLE'})}
                        />
                      </div>

                      <div className='mb-8'>
                        <label className='form-label fw-semibold text-gray-700'>
                          {intl.formatMessage({id: 'BLOG.SLUG'})}
                        </label>
                        <div className='input-group input-group-solid'>
                          <span className='input-group-text border-0 bg-light text-muted fs-7'>/</span>
                          <input
                            type='text'
                            className='form-control form-control-solid border-0'
                            value={formik.values.slug}
                            onChange={(e) => {
                              setSlugTouched(true)
                              formik.setFieldValue('slug', slugify(e.target.value))
                            }}
                          />
                        </div>
                        <div className='form-text'>{intl.formatMessage({id: 'BLOG.SLUG_HINT'})}</div>
                      </div>

                      <div className='mb-8'>
                        <label className='form-label required fw-semibold text-gray-700'>
                          {intl.formatMessage({id: 'BLOG.CONTENT'})}
                        </label>
                        <div className='blog-editor-shell rounded-3 overflow-hidden border border-gray-200'>
                          <ReactQuill
                            ref={quillRef}
                            theme='snow'
                            value={activeTranslation.content}
                            modules={quillModules}
                            onChange={(value) => setActiveTranslationField('content', value)}
                          />
                        </div>
                      </div>

                      <div className='mb-0'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <label className='form-label fw-semibold text-gray-700 mb-0'>
                            {intl.formatMessage({id: 'BLOG.EXCERPT'})}
                          </label>
                          <span className='badge badge-light fs-8'>
                            {activeTranslation.excerpt.length}/300
                          </span>
                        </div>
                        <textarea
                          className='form-control form-control-solid'
                          rows={4}
                          maxLength={300}
                          value={activeTranslation.excerpt}
                          onChange={(e) => setActiveTranslationField('excerpt', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-lg-4'>
                  <div className='d-flex flex-column gap-6'>
                    <div className='card border border-gray-200 shadow-sm'>
                      <div className='card-header border-0 pt-5 pb-0 bg-transparent'>
                        <h3 className='fs-5 fw-bold text-gray-900 mb-0 d-flex align-items-center gap-2'>
                          <i className='bi bi-send text-primary' />
                          {intl.formatMessage({id: 'BLOG.SECTION_PUBLISHING'})}
                        </h3>
                      </div>
                      <div className='card-body pt-5'>
                        <div className='mb-6'>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'BLOG.STATUS'})}
                          </label>
                          <select
                            className='form-select form-select-solid'
                            value={formik.values.status}
                            onChange={(e) => formik.setFieldValue('status', e.target.value)}
                          >
                            <option value='draft'>{intl.formatMessage({id: 'BLOG.DRAFT'})}</option>
                            <option value='published'>{intl.formatMessage({id: 'BLOG.PUBLISHED'})}</option>
                          </select>
                        </div>

                        <div className='mb-6'>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'BLOG.PUBLISH_DATE'})}
                          </label>
                          <input
                            type='date'
                            className='form-control form-control-solid'
                            value={formik.values.publishedAt}
                            onChange={(e) => formik.setFieldValue('publishedAt', e.target.value)}
                            disabled={formik.values.status !== 'published'}
                          />
                        </div>

                        <div className='mb-6'>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'BLOG.CATEGORY'})}
                          </label>
                          <select
                            className='form-select form-select-solid'
                            value={formik.values.category}
                            onChange={(e) => formik.setFieldValue('category', e.target.value)}
                          >
                            <option value=''>
                              {intl.formatMessage({id: 'BLOG.CATEGORY.NONE', defaultMessage: '— No category —'})}
                            </option>
                            {(categoriesData || []).map((category: {slug: string; title: string}) => (
                              <option key={category.slug} value={category.slug}>
                                {category.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className='mb-6'>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'BLOG.TAGS'})}
                          </label>
                          <div className='d-flex gap-2'>
                            <input
                              list='blog-tags-suggestions'
                              className='form-control form-control-solid'
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onPaste={(e) => {
                                const pasted = e.clipboardData.getData('text')
                                if (!/[,;\n]/.test(pasted)) return
                                e.preventDefault()
                                commitTagInput(`${tagInput} ${pasted}`)
                              }}
                              onBlur={() => {
                                if (tagInput.trim()) commitTagInput(tagInput)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ',') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  commitTagInput(tagInput)
                                }
                              }}
                              placeholder={intl.formatMessage({id: 'BLOG.TAGS_HINT'})}
                            />
                            <button
                              type='button'
                              className='btn btn-light-primary fw-semibold'
                              onClick={() => commitTagInput(tagInput)}
                            >
                              {intl.formatMessage({id: 'COMMON.ADD', defaultMessage: 'Add'})}
                            </button>
                          </div>
                          <datalist id='blog-tags-suggestions'>
                            {(tagsSuggestions || []).map((tag) => (
                              <option key={tag} value={tag} />
                            ))}
                          </datalist>
                          <div className='d-flex flex-wrap gap-2 mt-3'>
                            {activeTags.map((tag) => (
                              <span
                                key={tag}
                                className='badge badge-light-primary fs-8 fw-semibold py-2 px-3 d-inline-flex align-items-center gap-2'
                              >
                                {tag}
                                <button
                                  type='button'
                                  className='btn btn-icon btn-xs btn-active-color-danger p-0 ms-1'
                                  onClick={() => removeTag(tag)}
                                  aria-label={intl.formatMessage({id: 'COMMON.REMOVE'})}
                                >
                                  <i className='bi bi-x-lg fs-7' />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'BLOG.READING_TIME'})} (min)
                          </label>
                          <input
                            type='number'
                            className='form-control form-control-solid'
                            min={1}
                            value={formik.values.readingTime}
                            onChange={(e) => formik.setFieldValue('readingTime', Number(e.target.value || 1))}
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      ref={mainImageSectionRef}
                      className={`card border shadow-sm ${
                        mainImageMissingAttempt ? 'border-danger border-2' : 'border-gray-200'
                      }`}
                    >
                      <div className='card-header border-0 pt-5 pb-0 bg-transparent'>
                        <h3 className='fs-5 fw-bold text-gray-900 mb-0 d-flex align-items-center gap-2'>
                          <i className='bi bi-image text-primary' />
                          {intl.formatMessage({id: 'BLOG.MAIN_IMAGE'})}
                          {!isEditMode && <span className='text-danger'>*</span>}
                        </h3>
                      </div>
                      <div className='card-body pt-5'>
                        {mainImageMissingAttempt && (
                          <div className='alert alert-danger d-flex align-items-center gap-2 py-3 mb-5' role='alert'>
                            <i className='bi bi-exclamation-triangle-fill flex-shrink-0' />
                            <span>{intl.formatMessage({id: 'COMMON.ERROR.NO_IMAGE_SELECTED'})}</span>
                          </div>
                        )}
                        <p className='text-muted fs-7 mb-4'>
                          {intl.formatMessage({
                            id: 'BLOG.GALLERY_HINT',
                            defaultMessage:
                              'Add as many images as you need. The first is the cover. Save the article to apply changes.',
                          })}
                        </p>
                        <div
                            className={`blog-image-drop position-relative border border-2 border-dashed rounded-3 w-100 text-center bg-light-primary bg-opacity-10 ${
                              mainImageMissingAttempt ? 'border-danger' : isImageDropActive ? 'border-primary' : 'border-gray-300'
                            }`}
                            onDragEnter={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setIsImageDropActive(true)
                            }}
                            onDragOver={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setIsImageDropActive(true)
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setIsImageDropActive(false)
                            }}
                            onDrop={onGalleryDrop}
                          >
                            <input
                              ref={addImagesInputRef}
                              type='file'
                              accept='image/png,image/jpeg,image/jpg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif'
                              multiple
                              className='position-absolute top-0 start-0 w-100 h-100'
                              style={{opacity: 0, cursor: 'pointer', zIndex: 2}}
                              onChange={(e) => {
                                if (e.target.files?.length) addGalleryFiles(e.target.files)
                                e.target.value = ''
                              }}
                            />
                            <div className='p-8' style={{pointerEvents: 'none'}}>
                              <i className='bi bi-cloud-arrow-up fs-2x text-primary mb-3 d-block' />
                              <span className='text-gray-700 fw-semibold d-block mb-1'>
                                {intl.formatMessage({id: 'BLOG.UPLOAD_HINT'})}
                              </span>
                              <span className='text-muted fs-8'>
                                {intl.formatMessage({id: 'COMMUN.ALLOWED_FILE'})}
                                {gallery.length > 0
                                  ? ` · ${intl.formatMessage(
                                      {id: 'BLOG.IMAGES_COUNT'},
                                      {count: gallery.length},
                                    )}`
                                  : ''}
                              </span>
                            </div>
                          </div>
                        <div className='form-text text-muted mt-2 mb-0'>
                          {intl.formatMessage(
                            {id: 'COMMON.IMAGE.RECOMMENDED_SIZE'},
                            {size: recommendedSizeFor(gallery.length === 0 ? 'blog_cover' : 'blog_gallery')},
                          )}
                        </div>
                        {gallery.length > 0 && (
                          <div className='d-flex flex-column gap-4 mt-5'>
                            {gallery.map((slot, index) => (
                              <div key={slot.key} className='border border-gray-200 rounded-3 overflow-hidden shadow-sm'>
                                <div className='position-relative bg-light'>
                                  <img
                                    src={slot.preview}
                                    alt=''
                                    className='w-100 object-fit-cover'
                                    style={{maxHeight: 180, minHeight: 140, display: 'block'}}
                                  />
                                  {index === 0 && (
                                    <span className='badge badge-light-primary position-absolute top-0 start-0 m-3'>
                                      {intl.formatMessage({id: 'BLOG.COVER', defaultMessage: 'Cover'})}
                                    </span>
                                  )}
                                </div>
                                <div className='d-flex flex-wrap gap-2 p-3 bg-light'>
                                  <label className='btn btn-sm btn-light-primary fw-bold mb-0 position-relative overflow-hidden'>
                                    <i className='bi bi-arrow-repeat me-2' />
                                    {intl.formatMessage({id: 'BLOG.REPLACE_IMAGE', defaultMessage: 'Replace'})}
                                    <input
                                      type='file'
                                      accept='image/png,image/jpeg,image/jpg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif'
                                      className='position-absolute top-0 start-0 w-100 h-100'
                                      style={{opacity: 0, cursor: 'pointer'}}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0]
                                        if (file) replaceGallerySlot(slot.key, file)
                                        e.target.value = ''
                                      }}
                                    />
                                  </label>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-danger fw-bold'
                                    onClick={() => removeGallerySlot(slot.key)}
                                  >
                                    <i className='bi bi-trash me-2' />
                                    {intl.formatMessage({id: 'COMMON.REMOVE'})}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='card border border-gray-200 shadow-sm'>
                      <div className='card-header border-0 pt-5 pb-0 bg-transparent'>
                        <h3 className='fs-5 fw-bold text-gray-900 mb-0 d-flex align-items-center gap-2'>
                          <i className='bi bi-search text-primary' />
                          {intl.formatMessage({id: 'BLOG.SEO'})}
                        </h3>
                      </div>
                      <div className='card-body pt-5'>
                        <div className='mb-5'>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'COLUMNS.METE_TITLE'})}
                          </label>
                          <input
                            className='form-control form-control-solid'
                            value={activeTranslation.metaTitle}
                            onChange={(e) => setActiveTranslationField('metaTitle', e.target.value)}
                          />
                        </div>
                        <div className='mb-5'>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'COLUMNS.METE_DESCRIPTION'})}
                          </label>
                          <textarea
                            className='form-control form-control-solid'
                            rows={3}
                            value={activeTranslation.metaDescription}
                            onChange={(e) => setActiveTranslationField('metaDescription', e.target.value)}
                          />
                        </div>
                        <div className='mb-0'>
                          <label className='form-label fw-semibold text-gray-700'>
                            {intl.formatMessage({id: 'COLUMNS.META_KEYWORDS'})}
                          </label>
                          <input
                            className='form-control form-control-solid'
                            value={activeTranslation.metaKeywords}
                            onChange={(e) => setActiveTranslationField('metaKeywords', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className='position-sticky bottom-0 mt-10 py-5 rounded-top-3 shadow-lg d-flex flex-column flex-md-row flex-wrap justify-content-end gap-3 border border-gray-200 border-bottom-0 bg-body px-4 px-md-6'
                style={{zIndex: 101}}
              >
                <button
                  type='button'
                  className='btn btn-light fw-bold px-6 w-100 w-md-auto'
                  onClick={() => navigate('/apps/blog-management')}
                >
                  {intl.formatMessage({id: 'TOUR.ACTIONS.CANCEL', defaultMessage: 'Cancel'})}
                </button>
                <button
                  type='button'
                  className='btn btn-light-primary fw-bold px-6 w-100 w-md-auto'
                  onClick={() => void submitBlogWithStatus('draft')}
                >
                  <i className='bi bi-save me-2' />
                  {intl.formatMessage({id: 'BLOG.SAVE_DRAFT'})}
                </button>
                <button
                  type='button'
                  className='btn btn-primary fw-bold px-8 w-100 w-md-auto'
                  onClick={() => void submitBlogWithStatus('published')}
                >
                  <i className='bi bi-send-fill me-2' />
                  {intl.formatMessage({id: 'BLOG.PUBLISH'})}
                </button>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>
    </>
  )
}

export default BlogEditPage
